import { type FC, useCallback, useState } from "react";

import { AutoActionButton } from "./AutoActionButton";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { PillBannerButton, type PillBannerButtonProps } from "components/footer/PillBannerButton";
import { t } from "i18n/i18n";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { CrossRemoveIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentSectionId } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { viewStateActions } from "store/slices/view";
import { usePresentation } from "utils/presentation";
import { Link, useQueryParamsActions } from "utils/routing";
import { pageUrl, withQueryParams } from "utils/urls";

type FollowButtonsProps = {
  allowAutoFollow: boolean;
  screenSharing?: boolean;
};

export const FollowButtons: FC<FollowButtonsProps> = ({
  allowAutoFollow,
  screenSharing = false,
}) => {
  const { canParticipateInSection } = useCapabilities();
  const dispatch = useAppDispatch();
  const { setQueryParams } = useQueryParamsActions();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const { presentedPageId } = usePresentation();
  const [dismissed, setDismissed] = useState(!allowAutoFollow);
  const showShortText = !useIsLgOrLarger();

  const eventPrefix = screenSharing ? "Screensharing" : "Presentation";

  const buttonProps =
    screenSharing || !currentSectionId || !presentedPageId
      ? {}
      : ({
          as: Link,
          to: withQueryParams(pageUrl(currentSectionId, presentedPageId), {
            [FOLLOWING_QUERY_PARAM]: "1",
          }),
        } as PillBannerButtonProps<typeof Link>);

  const handleClick = useCallback(() => {
    if (screenSharing) {
      dispatch(viewStateActions.setViewingScreenshare(true));
      setQueryParams({ [FOLLOWING_QUERY_PARAM]: "1" });
    } else if (canParticipateInSection) {
      dispatch(localStateActions.setIsSidebarOpen(false));
    }
  }, [dispatch, setQueryParams, screenSharing, canParticipateInSection]);

  return dismissed ? (
    <PillBannerButton
      eventName={`${eventPrefix} - Start following`}
      kind="banner-green"
      onClick={handleClick}
      {...buttonProps}
    >
      {showShortText ? t("main_header.follow_short") : t("main_header.follow")}
    </PillBannerButton>
  ) : (
    <div className="flex items-center gap-1">
      <PillBannerButton
        className="border border-green"
        eventName={`${eventPrefix} - Dismiss Auto-Follow`}
        kind="green"
        onClick={() => setDismissed(true)}
      >
        {showShortText ? <CrossRemoveIcon size="s" /> : t("common.dismiss")}
      </PillBannerButton>

      <AutoActionButton
        eventName={`${eventPrefix} - Start following`}
        onClick={handleClick}
        {...buttonProps}
      >
        {t(showShortText ? "main_header.start_following_short" : "main_header.start_following")}
      </AutoActionButton>
    </div>
  );
};
